import React, { useState, useEffect } from 'react';
import { Eye, Check, X, Mail, Phone, Calendar, Search } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminContacts() {
  const { t } = useLanguage();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/admin/contacts`);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/admin/contacts/${id}`, { is_read: true });
      setContacts(contacts.map(c => c.id === id ? { ...c, is_read: true } : c));
      toast.success('Marqué comme lu');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`);
      setContacts(contacts.filter(c => c.id !== id));
      toast.success('Message supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredContacts = contacts.filter(c => {
    const matchesFilter = filter === 'all' || (filter === 'unread' && !c.is_read) || (filter === 'read' && c.is_read);
    const matchesSearch = !search || 
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase()) ||
      c.subject.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <div data-testid="admin-contacts">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">
          {t('admin_contacts')}
        </h1>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Rechercher..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="unread">Non lus</SelectItem>
            <SelectItem value="read">Lus</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B6AB3]"></div>
        </div>
      ) : filteredContacts.length === 0 ? (
        <div className="text-center py-20 text-gray-500">
          Aucun message trouvé
        </div>
      ) : (
        <div className="space-y-3">
          {filteredContacts.map((contact, index) => (
            <Card
              key={contact.id}
              className={`border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${!contact.is_read ? 'bg-blue-50/50 border-l-4 border-l-[#0B6AB3]' : ''}`}
              onClick={() => { setSelectedContact(contact); if (!contact.is_read) markAsRead(contact.id); }}
              data-testid={`contact-item-${index}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className={`font-semibold text-[#0F172A] ${!contact.is_read ? 'font-bold' : ''}`}>
                        {contact.name}
                      </h3>
                      {!contact.is_read && <Badge className="bg-[#0B6AB3]">Nouveau</Badge>}
                    </div>
                    <p className="text-sm text-gray-600 mb-1">{contact.subject}</p>
                    <p className="text-sm text-gray-500 truncate">{contact.message}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {contact.email}
                      </span>
                      {contact.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {contact.phone}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(contact.created_at).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); handleDelete(contact.id); }}
                    className="text-red-500"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={!!selectedContact} onOpenChange={() => setSelectedContact(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Message de {selectedContact?.name}</DialogTitle>
          </DialogHeader>
          {selectedContact && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-gray-500">Email</span>
                  <p className="font-medium">{selectedContact.email}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Téléphone</span>
                  <p className="font-medium">{selectedContact.phone || 'Non renseigné'}</p>
                </div>
              </div>
              <div>
                <span className="text-sm text-gray-500">Sujet</span>
                <p className="font-medium">{selectedContact.subject}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Message</span>
                <p className="whitespace-pre-wrap">{selectedContact.message}</p>
              </div>
              <div className="text-sm text-gray-500">
                Reçu le {new Date(selectedContact.created_at).toLocaleString('fr-FR')}
              </div>
              <div className="flex gap-2 pt-4">
                <a href={`mailto:${selectedContact.email}`}>
                  <Button className="bg-[#0B6AB3] hover:bg-[#095c9e] text-white">
                    <Mail className="h-4 w-4 mr-2" />
                    Répondre par email
                  </Button>
                </a>
                {selectedContact.phone && (
                  <a href={`tel:${selectedContact.phone}`}>
                    <Button variant="outline">
                      <Phone className="h-4 w-4 mr-2" />
                      Appeler
                    </Button>
                  </a>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
